<!--
  PayrollModal.vue

  Purpose: Thin orchestrator for payroll wizard using XState machine as SOLE controller

  Architecture (Post-Migration with WizardModalModern (machine mode)):
  - XState machine (usePayrollWizardMachine) is the SINGLE SOURCE OF TRUTH
  - WizardModalModern (machine mode) renders UI based ENTIRELY on machine state
  - NO internal step counter - eliminates sync issues completely
  - Machine is provided to children via provide('wizardMachine', machine)
  - ALL step components use inject('wizardMachine') to access data

  Responsibilities:
  - Import and instantiate machine
  - Provide machine to children via provide()
  - Handle modal open/close via eventBus
  - Pass machine to WizardModalModern (machine mode) (it handles all navigation)

  Data Flow:
  - machine → WizardModalModern (machine mode) (derives ALL state from machine)
  - Step components: inject('wizardMachine').context.value.X → Reactive data
-->
<template>
  <template v-if="isVisible">
    <WizardModalModern
      :machine="machine"
      :show-modal="visible"
      :modal-title="modalTitle"
      size="xlarge"
      :allow-step-click="true"
      @close="handleClose"
      @cancel="handleClose"
    ></WizardModalModern>
  </template>
</template>

<script setup lang="ts">
import { ref, computed, provide, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { log } from '@/utils/DebugLogger';
import { eventBus, type EventCallback } from '@/eventBus';
import WizardModalModern from '@/ui/WizardModalModern.vue';
import { usePayrollWizardMachine } from '@/composables/usePayrollWizardMachine';
import { usePayrollPeriods } from '@/composables/usePayrollPeriods';

type Id = number | string;

interface OpenPayload {
  periodId?: Id;
  franchiseId?: Id;
  /** Legacy format */
  period?: { id: Id; franchise_id: Id };
  prefill?: Record<string, unknown>;
}

const { t } = useI18n();
const { getPeriod } = usePayrollPeriods();

// =============================================================================
// XSTATE MACHINE - Single Source of Truth
// =============================================================================

const machine = usePayrollWizardMachine();

// Provide machine to all child components via inject('wizardMachine')
provide('wizardMachine', machine);

// =============================================================================
// MODAL STATE
// =============================================================================

const isVisible = ref(false);
const visible = ref(true);

// =============================================================================
// COMPUTED PROPERTIES FROM MACHINE
// =============================================================================

const modalTitle = computed((): string => {
  return machine.isEditMode.value
    ? t('Continuar o período de folha de pagamento')
    : t('Criar período de folha de pagamento');
});

// =============================================================================
// EVENT HANDLERS
// =============================================================================

/**
 * Handle modal close
 * WizardModalModern (machine mode) handles all navigation internally,
 * we just need to update our visibility state
 */
const handleClose = (): void => {
  log('PayrollModal', 'handleClose', 'Closing modal');
  machine.cancel();
  visible.value = false;
  isVisible.value = false;
};

// =============================================================================
// MODAL INITIALIZATION
// =============================================================================

/**
 * Handle modal open
 *
 * Architecture: Both CREATE and EDIT flows now use the same pattern:
 * 1. Receive periodId + franchiseId
 * 2. Fetch full period data from API (single source of truth)
 * 3. Initialize wizard machine with fetched data
 */
const handleOpen: EventCallback<OpenPayload> = async (data): Promise<void> => {
  log('PayrollModal', 'handleOpen', 'Opening modal', {
    periodId: data?.periodId ?? data?.period?.id,
    franchiseId: data?.franchiseId ?? data?.period?.franchise_id,
    hasLegacyPeriod: !!data?.period,
    hasPrefill: !!data?.prefill
  });

  // Determine period ID and franchise ID (support both new and legacy formats)
  const periodId = data?.periodId ?? data?.period?.id;
  const franchiseId = data?.franchiseId ?? data?.period?.franchise_id;

  // If we have a period ID, fetch fresh data from API
  if (periodId && franchiseId) {
    try {
      log('PayrollModal', 'handleOpen', 'Fetching period from API', { periodId, franchiseId });

      // Fetch period data from API - this is the SINGLE SOURCE OF TRUTH
      const period = await getPeriod(periodId, franchiseId) as unknown as {
        id: Id;
        period_start_date: string;
        period_end_date: string;
        status: number;
        franchise_id: Id;
        [key: string]: unknown;
      };

      log('PayrollModal', 'handleOpen', 'Period fetched successfully', {
        periodId: period.id,
        start_date: period.period_start_date,
        end_date: period.period_end_date,
        status: period.status
      });

      // Initialize machine with fetched data
      machine.resumePeriod(period);
    } catch (err) {
      const error = err as Error;
      console.error('[PayrollModal] Failed to fetch period:', error);
      eventBus.emit('globalMessage.error', {
        message: error.message || 'Erro ao carregar período',
        duration: 5000
      });
      return; // Don't open modal if fetch failed
    }
  } else {
    // No period ID - start new (this path is for future use if needed)
    machine.startNew();

    // If prefill data provided, update form after starting
    if (data?.prefill) {
      await nextTick();
      machine.updateForm(data.prefill);
      log('PayrollModal', 'handleOpen', 'Applied prefill data', data.prefill);
    }
  }

  // Show modal - WizardModalModern (machine mode) derives everything from machine
  visible.value = true;
  isVisible.value = true;
};

// =============================================================================
// LIFECYCLE
// =============================================================================

onMounted(() => {
  log('PayrollModal', 'onMounted', 'Modal mounted');
  eventBus.on('modal.payroll.open', handleOpen);
});

onBeforeUnmount(() => {
  log('PayrollModal', 'onBeforeUnmount', 'Cleaning up');
  eventBus.off('modal.payroll.open', handleOpen);
});
</script>

<style scoped>
/* Styles in individual step components */
</style>
